import React from 'react';
import CsvButton from '../CsvButton';
import { DIRECTION } from '../lib/colors';
import { fmtCount, fmtDate, fmtSearchTerm } from '../lib/format';

/**
 * One diverging bar. Defined at module scope, not inside `MoverBars`: a component
 * redeclared on every render is a new type each time, so React unmounts and remounts the
 * whole subtree instead of updating it — losing DOM state and focus for no reason.
 */
const MoverRow = ({ row, gained, widest, onDrill }) => {
  const delta = Number(row.delta) || 0;
  const width = `${(Math.abs(delta) / widest) * 100}%`;
  const drillable = Boolean(row?.drill) && typeof onDrill === 'function';
  const body = (
    <>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="text-gray-800 dark:text-gray-200 break-words min-w-0">{fmtSearchTerm(row.query)}</span>
        <span
          className="shrink-0 inline-flex items-center gap-1 font-medium tabular-nums"
          style={{ color: gained ? DIRECTION.improved : DIRECTION.declined }}
        >
          <span aria-hidden="true">{gained ? '▲' : '▼'}</span>
          {fmtCount(Math.abs(delta))}
          <span className="text-xs font-normal">{gained ? 'more visits' : 'fewer visits'}</span>
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-800" aria-hidden="true">
        <div
          className="h-full rounded-full"
          style={{ width, backgroundColor: gained ? DIRECTION.improved : DIRECTION.declined }}
        />
      </div>
      <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
        {fmtCount(row.clicks_prior)} → {fmtCount(row.clicks_now)} visits
      </p>
    </>
  );
  if (drillable) {
    return (
      <li>
        <button
          type="button"
          data-query={row.query}
          onClick={() => onDrill(row.drill)}
          className="w-full py-1.5 px-2 -mx-2 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-inset transition-colors"
        >
          {body}
        </button>
      </li>
    );
  }
  return <li className="py-1.5" data-query={row.query}>{body}</li>;
};

/**
 * Evidence for `movers`.
 *
 * `facts`: { gainers: [{ query, clicks_now, clicks_prior, delta, impressions_now }],
 *            decliners: [ …same… ],
 *            summary: { comparable, prior_period_start, prior_period_end,
 *                       prior_days_covered, days_requested } }
 *
 * Renders NOTHING but the honest explanation when `summary.comparable === false` — a
 * property with no prior-year data has no movers, and drawing empty axes would suggest
 * every search held perfectly flat. The live Verde Ranch payload is exactly this case.
 *
 * Bar length is `|delta|` scaled against the largest absolute delta on screen. That is
 * a layout proportion; every number printed is a returned value (F2).
 *
 * Collapsed is the diverging bars; expanded adds the CSV of both lists with each
 * period's own count (spec v2 §7).
 */

/**
 * Raw payload values for the spreadsheet — see `lib/csv.js`.
 *
 * `delta` is exported as it ARRIVES rather than recomputed from the two click counts:
 * both are in the row and the backend published the difference alongside them, so
 * subtracting them here would be inventing a number that already exists (§8).
 */
const CSV_COLUMNS = [
  { header: 'Search', get: (r) => fmtSearchTerm(r.query) },
  { header: 'Direction', get: (r) => (Number(r.delta) >= 0 ? 'more visits' : 'fewer visits') },
  { header: 'Visits this period', get: (r) => r.clicks_now },
  { header: 'Visits prior period', get: (r) => r.clicks_prior },
  { header: 'Change in visits', get: (r) => r.delta },
  { header: 'Showed up in searches this period', get: (r) => r.impressions_now },
];

const MoverBars = ({ facts, collapsed = true, onDrill }) => {
  const summary = facts?.summary || {};
  const gainers = Array.isArray(facts?.gainers) ? facts.gainers : [];
  const decliners = Array.isArray(facts?.decliners) ? facts.decliners : [];

  if (summary.comparable === false) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {fmtDate(summary.prior_period_start) && fmtDate(summary.prior_period_end)
          ? `We have no search data for ${fmtDate(summary.prior_period_start)} – ${fmtDate(summary.prior_period_end)}, so there is nothing to compare this period against yet.`
          : 'There is no matching period from last year to compare against yet.'}
      </p>
    );
  }

  const rows = [...gainers, ...decliners];
  if (rows.length === 0) return null;

  const widest = rows.reduce((max, r) => Math.max(max, Math.abs(Number(r.delta) || 0)), 0) || 1;

  return (
    <div className="space-y-4">
      {gainers.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            More visits than a year ago
          </p>
          <ul className="mt-1">
            {gainers.map((row, i) => <MoverRow key={`${row.query}-g${i}`} row={row} gained widest={widest} onDrill={onDrill} />)}
          </ul>
        </div>
      )}
      {decliners.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Fewer visits than a year ago
          </p>
          <ul className="mt-1">
            {decliners.map((row, i) => <MoverRow key={`${row.query}-d${i}`} row={row} gained={false} widest={widest} onDrill={onDrill} />)}
          </ul>
        </div>
      )}
      {fmtDate(summary.prior_period_start) && fmtDate(summary.prior_period_end) && (
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Compared against {fmtDate(summary.prior_period_start)} – {fmtDate(summary.prior_period_end)}.
        </p>
      )}
      {!collapsed && (
        <CsvButton
          panelKey="movers"
          name="What changed since last year"
          columns={CSV_COLUMNS}
          rows={rows}
          range={{ from: summary.prior_period_start, to: summary.prior_period_end }}
        />
      )}
    </div>
  );
};

/**
 * Whether opening this card would change what is on screen (spec v2 §3).
 *
 * Expanding adds the CSV of both lists with each period's own count. A payload the
 * backend has marked not comparable renders only an explanatory sentence, which is not
 * something to export — and there is nothing else behind it.
 *
 * @param {object} facts the panel's `facts`
 * @returns {boolean} true when expansion reveals something
 */
MoverBars.hasDepth = (facts) => {
  if (facts?.summary?.comparable === false) return false;
  const rows = [
    ...(Array.isArray(facts?.gainers) ? facts.gainers : []),
    ...(Array.isArray(facts?.decliners) ? facts.decliners : []),
  ];
  return rows.length > 0;
};


export default MoverBars;
